'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, Modal } from '@common/components';
import { cn, isEdge } from '@core/utils';
import { useSuspenseQuery } from '@tanstack/react-query';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useState, useTransition } from 'react';
import { UnauthQueryOptions } from '@/api';
import { ResponsiveDialog } from '@/components/organism/ResponsiveDialog';
import { DONATION_ROUTES, ROUTES, STORAGE_KEY } from '@/constants';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useVerificationCheck } from '@/hooks/useVerificationCheck';
import { useAuthStore } from '@/stores/AuthStore';
import { useDebugStore } from '@/stores/DebugStore';
import { gtmDatasets } from '@/utils/analytics/gtm';
import { DonationDebugPanel } from '../DebugPanel';
import { DonationReservationCompleteModal } from '../DonationReservationCompleteModal';
import { DonationReservationModal } from '../DonationReservationModal';
import { useIsDonationHistory } from './_hooks/useIsDonationHistory';
import { AgreementSection } from './AgreementSection';
import { PrefillForm } from './PrefillForm';
interface DonationButtonProps {
  institutionId: number;
  donationBusinessId?: number;
  institutionName: string;
  disabled?: boolean;
  children?: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
}
export const DonationButton = t0 => {
  const $ = _c(105);
  const {
    institutionId,
    donationBusinessId,
    institutionName,
    disabled,
    children,
    style,
    className
  } = t0;
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  const isLogin = useAuthStore(_temp);
  const {
    isDonationHistory
  } = useIsDonationHistory();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = isEdge();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const isEdgeBrowser = t1;
  const [, startTransition] = useTransition();
  const [isReservationModalOpen, setIsReservationModalOpen] = useState(false);
  const [isReservationCompleteModalOpen, setIsReservationCompleteModalOpen] = useState(false);
  const router = useCustomRouter();
  let t2;
  if ($[1] !== isLogin) {
    t2 = {
      enabled: isLogin
    };
    $[1] = isLogin;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    checkVerification,
    isLoading: isVerificationLoading
  } = useVerificationCheck(t2);
  const pathname = usePathname();
  const [step, setStep] = useState("closed");
  const [isEdgeModal, setIsEdgeModal] = useState(false);
  const skipReservation = useDebugStore(_temp2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = UnauthQueryOptions["get:/wello/v2/allows/control-managements"]();
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== skipReservation) {
    t4 = {
      ...t3,
      select: data => skipReservation ? null : data.context,
      staleTime: 0,
      gcTime: 0
    };
    $[4] = skipReservation;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const {
    data: donationReservation
  } = useSuspenseQuery(t4);
  let t5;
  if ($[6] !== checkVerification || $[7] !== donationReservation || $[8] !== isLogin || $[9] !== pathname || $[10] !== router) {
    t5 = () => {
      if (isEdgeBrowser) {
        setIsEdgeModal(true);
        return;
      }
      if (!isLogin) {
        router.push(ROUTES.LOGIN.withSearchParams({
          searchParams: {
            redirect: pathname ?? ""
          }
        }));
        return;
      }
      if (checkVerification()) {
        return;
      }
      if (donationReservation) {
        setStep("closed");
        setIsReservationModalOpen(true);
        return;
      }
      setStep("product-check");
    };
    $[6] = checkVerification;
    $[7] = donationReservation;
    $[8] = isLogin;
    $[9] = pathname;
    $[10] = router;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handleStart = t5;
  let t6;
  if ($[12] !== isDonationHistory) {
    t6 = () => {
      const isAgreement = sessionStorage.getItem(STORAGE_KEY.DONATION_AGREEMENT);
      if (!isDonationHistory && !isAgreement) {
        setStep("agreement");
        return;
      }
      setStep("prefill");
    };
    $[12] = isDonationHistory;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const handleDonationWithoutGift = t6;
  let t7;
  if ($[14] !== donationBusinessId || $[15] !== institutionId || $[16] !== institutionName || $[17] !== pathname || $[18] !== router) {
    t7 = () => {
      sessionStorage.setItem(STORAGE_KEY.DONATION_AGREEMENT, "true");
      router.push(DONATION_ROUTES.HOMETOWN_BUSINESS_ORDER.withSearchParams({
        searchParams: {
          donationBusinessId,
          institutionId,
          institutionName,
          from: pathname ?? undefined
        }
      }));
    };
    $[14] = donationBusinessId;
    $[15] = institutionId;
    $[16] = institutionName;
    $[17] = pathname;
    $[18] = router;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const handleDonationWithoutGiftAfterAgreement = t7;
  let t8;
  if ($[20] !== className) {
    t8 = cn("w-full", className);
    $[20] = className;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const t9 = children || "\uAE30\uBD80\uD558\uAE30";
  let t10;
  if ($[22] !== disabled || $[23] !== handleStart || $[24] !== isVerificationLoading || $[25] !== style || $[26] !== t8 || $[27] !== t9) {
    t10 = <Button loading={isVerificationLoading} onClick={handleStart} className={t8} disabled={disabled} style={style}>{t9}</Button>;
    $[22] = disabled;
    $[23] = handleStart;
    $[24] = isVerificationLoading;
    $[25] = style;
    $[26] = t8;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  const t11 = step === "agreement";
  let t12;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => setStep("closed");
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      title: "\uACE0\uD5A5\uC0AC\uB791\uAE30\uBD80 \uC11C\uBE44\uC2A4\uB97C \uC774\uC6A9\uD558\uAE30 \uC704\uD574 \uAF2D \uD544\uC694\uD55C \uB3D9\uC758\uB9CC \uCD94\uB838\uC5B4\uC694"
    };
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== handleDonationWithoutGiftAfterAgreement) {
    t14 = <AgreementSection onNext={handleDonationWithoutGiftAfterAgreement} />;
    $[31] = handleDonationWithoutGiftAfterAgreement;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== t11 || $[34] !== t14) {
    t15 = <ResponsiveDialog opened={t11} onClose={t12} drawerType="bottom" title={"\uACE0\uD5A5\uC0AC\uB791\uAE30\uBD80 \uC11C\uBE44\uC2A4\uB97C \uC774\uC6A9\uD558\uAE30 \uC704\uD574 \uAF2D \uD544\uC694\uD55C \uB3D9\uC758\uB9CC \uCD94\uB838\uC5B4\uC694"} modalHeader={t13}>{t14}</ResponsiveDialog>;
    $[33] = t11;
    $[34] = t14;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  const t16 = step === "product-check";
  let t17;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => setStep("closed");
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      title: "\uAE30\uBD80 \uAE08\uC561\uC5D0 \uB9DE\uB294\n\uB2F5\uB840\uD488\uC744 \uC120\uD0DD\uD574 \uC8FC\uC138\uC694",
      description: "\uACE0\uD5A5\uC0AC\uB791\uAE30\uBD80\uAE08\uC758 30% \uB0B4\uC5D0\uC11C \uB2F5\uB840\uD488\uC744 \uBB34\uB8CC\uB85C \uBC1B\uC544\uBCF4\uC138\uC694!"
    };
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== isDesktopViewport) {
    t19 = !isDesktopViewport ? <><h3 className="mb-26 break-keep pb-12 text-center font-700 text-Gray-900 typo-body1">기부 금액에 맞는<br />답례품을 선택해 주세요</h3><p className="mb-32 text-center font-400 text-Gray-800 typo-body3">고향사랑기부금의 30% 내에서<br />답례품을 무료로 받아보세요!</p></> : null;
    $[38] = isDesktopViewport;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  const t20 = isDesktopViewport ? "H56" : "H48";
  let t21;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = () => setStep("closed");
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== institutionId) {
    t22 = DONATION_ROUTES.HOMETOWNS_DETAIL.withSearchParams({
      dynamicPath: {
        id: institutionId
      },
      searchParams: {
        tab: "gifts"
      }
    });
    $[41] = institutionId;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  let t23;
  if ($[43] !== institutionName) {
    t23 = gtmDatasets.startGift({
      local_name: institutionName
    });
    $[43] = institutionName;
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  let t24;
  if ($[45] !== t22 || $[46] !== t23) {
    t24 = <Link onClick={t21} href={t22} {...t23}>답례품 선택 후 기부하기</Link>;
    $[45] = t22;
    $[46] = t23;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  let t25;
  if ($[48] !== t20 || $[49] !== t24) {
    t25 = <Button dimensions={t20} asChild={true}>{t24}</Button>;
    $[48] = t20;
    $[49] = t24;
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  const t26 = isDesktopViewport ? "H56" : "H48";
  let t27;
  if ($[51] !== institutionName) {
    t27 = gtmDatasets.startDonation({
      local_name: institutionName
    });
    $[51] = institutionName;
    $[52] = t27;
  } else {
    t27 = $[52];
  }
  let t28;
  if ($[53] !== handleDonationWithoutGift || $[54] !== t26 || $[55] !== t27) {
    t28 = <Button dimensions={t26} theme="sub-text" onClick={handleDonationWithoutGift} {...t27}>답례품 없이 기부하기</Button>;
    $[53] = handleDonationWithoutGift;
    $[54] = t26;
    $[55] = t27;
    $[56] = t28;
  } else {
    t28 = $[56];
  }
  let t29;
  if ($[57] !== t25 || $[58] !== t28) {
    t29 = <div className="flex flex-col gap-8">{t25}{t28}</div>;
    $[57] = t25;
    $[58] = t28;
    $[59] = t29;
  } else {
    t29 = $[59];
  }
  let t30;
  if ($[60] !== t16 || $[61] !== t19 || $[62] !== t29) {
    t30 = <ResponsiveDialog opened={t16} onClose={t17} drawerType="bottom" modalHeader={t18}>{t19}{t29}</ResponsiveDialog>;
    $[60] = t16;
    $[61] = t19;
    $[62] = t29;
    $[63] = t30;
  } else {
    t30 = $[63];
  }
  const t31 = step === "prefill";
  let t32;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = () => setStep("closed");
    $[64] = t32;
  } else {
    t32 = $[64];
  }
  let t33;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = {
      title: "\uAE30\uBD80\uAE08 \uC120\uD0DD"
    };
    $[65] = t33;
  } else {
    t33 = $[65];
  }
  let t34;
  if ($[66] !== donationBusinessId || $[67] !== institutionId || $[68] !== institutionName || $[69] !== pathname || $[70] !== router) {
    t34 = data_0 => {
      setStep("closed");
      sessionStorage.setItem(STORAGE_KEY.DONATION_AGREEMENT, "true");
      router.push(DONATION_ROUTES.HOMETOWN_BUSINESS_ORDER.withSearchParams({
        searchParams: {
          donationBusinessId,
          institutionId,
          institutionName,
          initialAmount: data_0.amount,
          initialPurpose: data_0.purpose,
          from: pathname ?? undefined
        }
      }));
    };
    $[66] = donationBusinessId;
    $[67] = institutionId;
    $[68] = institutionName;
    $[69] = pathname;
    $[70] = router;
    $[71] = t34;
  } else {
    t34 = $[71];
  }
  let t35;
  if ($[72] !== donationBusinessId || $[73] !== institutionId || $[74] !== t34) {
    t35 = <PrefillForm institutionId={institutionId} defaultDonationBusinessId={donationBusinessId} onSubmit={t34} />;
    $[72] = donationBusinessId;
    $[73] = institutionId;
    $[74] = t34;
    $[75] = t35;
  } else {
    t35 = $[75];
  }
  let t36;
  if ($[76] !== t31 || $[77] !== t35) {
    t36 = <ResponsiveDialog opened={t31} onClose={t32} drawerType="bottom" modalHeader={t33} title={"\uAE30\uBD80\uAE08 \uC120\uD0DD"}>{t35}</ResponsiveDialog>;
    $[76] = t31;
    $[77] = t35;
    $[78] = t36;
  } else {
    t36 = $[78];
  }
  let t37;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = () => setIsEdgeModal(false);
    $[79] = t37;
  } else {
    t37 = $[79];
  }
  let t38;
  let t39;
  if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <h2 className="font-700 text-Gray-900 typo-body2">웰로의 고향사랑기부제는 엣지 브라우저에서 지원되지 않습니다.</h2>;
    t39 = <p className="font-400 text-Gray-700 typo-body3">원활한 이용을 위해 Chrome, Safari 등의 브라우저를 이용해 주세요.</p>;
    $[80] = t38;
    $[81] = t39;
  } else {
    t38 = $[80];
    t39 = $[81];
  }
  let t40;
  if ($[82] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <div className="flex flex-col gap-16 text-center">{t38}{t39}<Button onClick={() => setIsEdgeModal(false)}>확인</Button></div>;
    $[82] = t40;
  } else {
    t40 = $[82];
  }
  let t41;
  if ($[83] !== isEdgeModal) {
    t41 = <Modal opened={isEdgeModal} onClose={t37}>{t40}</Modal>;
    $[83] = isEdgeModal;
    $[84] = t41;
  } else {
    t41 = $[84];
  }
  let t42;
  if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = () => setIsReservationModalOpen(false);
    $[85] = t42;
  } else {
    t42 = $[85];
  }
  let t43;
  if ($[86] !== startTransition) {
    t43 = () => {
      setIsReservationModalOpen(false);
      startTransition(() => {
        setIsReservationCompleteModalOpen(true);
      });
    };
    $[86] = startTransition;
    $[87] = t43;
  } else {
    t43 = $[87];
  }
  const t44 = donationReservation?.contents;
  let t45;
  if ($[88] !== donationBusinessId || $[89] !== isReservationModalOpen || $[90] !== t43 || $[91] !== t44) {
    t45 = <DonationReservationModal opened={isReservationModalOpen} businessId={donationBusinessId} onClose={t42} onSuccess={t43} description={t44} />;
    $[88] = donationBusinessId;
    $[89] = isReservationModalOpen;
    $[90] = t43;
    $[91] = t44;
    $[92] = t45;
  } else {
    t45 = $[92];
  }
  let t46;
  if ($[93] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = () => setIsReservationCompleteModalOpen(false);
    $[93] = t46;
  } else {
    t46 = $[93];
  }
  let t47;
  if ($[94] !== isReservationCompleteModalOpen) {
    t47 = <DonationReservationCompleteModal opened={isReservationCompleteModalOpen} onClose={t46} />;
    $[94] = isReservationCompleteModalOpen;
    $[95] = t47;
  } else {
    t47 = $[95];
  }
  let t48;
  if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = <DonationDebugPanel />;
    $[96] = t48;
  } else {
    t48 = $[96];
  }
  let t49;
  if ($[97] !== t10 || $[98] !== t15 || $[99] !== t30 || $[100] !== t36 || $[101] !== t41 || $[102] !== t45 || $[103] !== t47) {
    t49 = <>{t10}{t15}{t30}{t36}{t41}{t45}{t47}{t48}</>;
    $[97] = t10;
    $[98] = t15;
    $[99] = t30;
    $[100] = t36;
    $[101] = t41;
    $[102] = t45;
    $[103] = t47;
    $[104] = t49;
  } else {
    t49 = $[104];
  }
  return t49;
};
function _temp(state) {
  return !!state.isLogin;
}
function _temp2(s) {
  return s.skipReservation;
}